import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { PlusIcon, StopCircleIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Rentals() {
  const [rentals, setRentals] = useState([]);
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stoppingId, setStoppingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newRental, setNewRental] = useState({
    deviceId: '',
    customerId: '',
    startDate: new Date().toISOString().split('T')[0],
    billingPeriod: 'MONTHLY',
    amountPerPeriod: 0,
    depositAmount: 0,
    graceDays: 3
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [rentalsRes, devicesRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/rentals`, { headers }),
        axios.get(`${API}/admin/devices`, { headers }),
        axios.get(`${API}/admin/customers`, { headers })
      ]);
      
      setRentals(rentalsRes.data);
      setDevices(devicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRental = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/rentals`, {
        ...newRental,
        startDate: new Date(newRental.startDate).toISOString(),
        amountPerPeriod: parseFloat(newRental.amountPerPeriod),
        depositAmount: parseFloat(newRental.depositAmount),
        graceDays: parseInt(newRental.graceDays)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rental created successfully');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create rental');
    }
  };

  const handleStopRental = async (rentalId) => {
    setStoppingId(rentalId);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/rentals/${rentalId}/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rental stopped successfully');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to stop rental';
      toast.error(message);
    } finally {
      setStoppingId(null);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getDeviceModel = (deviceId) => {
    const device = devices.find(d => d.id === deviceId);
    return device ? device.model : 'Unknown';
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      OVERDUE: 'bg-orange-100 text-orange-800',
      CLOSED: 'bg-gray-100 text-gray-800',
      CANCELLED: 'bg-red-100 text-red-800',
      TERMINATED: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const isRentalActive = (status) => {
    return status === 'ACTIVE' || status === 'OVERDUE';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="rentals-page">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Rentals</h1>
          <p className="text-gray-600 mt-2">Manage rental agreements</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-rental-button">
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Rental
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Rental</DialogTitle>
              <DialogDescription>Enter rental agreement details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRental} className="space-y-4">
              <div>
                <Label htmlFor="device">Device</Label>
                <Select value={newRental.deviceId} onValueChange={(value) => setNewRental({...newRental, deviceId: value})}>
                  <SelectTrigger data-testid="rental-device-select">
                    <SelectValue placeholder="Select device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.filter(d => !d.assignedCustomerId).map(device => (
                      <SelectItem key={device.id} value={device.id}>{device.model} - {device.serialNumber}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="customer">Customer</Label>
                <Select value={newRental.customerId} onValueChange={(value) => setNewRental({...newRental, customerId: value})}>
                  <SelectTrigger data-testid="rental-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newRental.startDate}
                  onChange={(e) => setNewRental({...newRental, startDate: e.target.value})}
                  required
                  data-testid="rental-start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="billingPeriod">Billing Period</Label>
                <Select value={newRental.billingPeriod} onValueChange={(value) => setNewRental({...newRental, billingPeriod: value})}>
                  <SelectTrigger data-testid="rental-billing-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Amount per Period (₹)</Label>
                <Input
                  id="amount"
                  type="number"
                  value={newRental.amountPerPeriod}
                  onChange={(e) => setNewRental({...newRental, amountPerPeriod: e.target.value})}
                  required
                  data-testid="rental-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="deposit">Deposit Amount (₹)</Label>
                <Input
                  id="deposit"
                  type="number"
                  value={newRental.depositAmount}
                  onChange={(e) => setNewRental({...newRental, depositAmount: e.target.value})}
                  data-testid="rental-deposit-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="create-rental-button">Create Rental</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {rentals.map((rental) => (
          <Card key={rental.id} className="hover:shadow-lg transition-shadow" data-testid={`rental-card-${rental.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{getDeviceModel(rental.deviceId)}</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">{getCustomerName(rental.customerId)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(rental.status)} data-testid={`rental-status-${rental.id}`}>
                    {rental.status}
                  </Badge>
                  {isRentalActive(rental.status) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          disabled={stoppingId === rental.id}
                          data-testid={`stop-rental-${rental.id}`}
                        >
                          <StopCircleIcon className="h-4 w-4 mr-1" />
                          Stop
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Stop This Rental?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will immediately terminate the rental for <strong>{getDeviceModel(rental.deviceId)}</strong>. 
                            The device will be detached from the customer and marked as available.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleStopRental(rental.id)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`confirm-stop-rental-${rental.id}`}
                          >
                            Stop Rental
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Billing Period</p>
                  <p className="font-medium">{rental.billingPeriod}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-medium">₹{rental.amountPerPeriod}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deposit</p>
                  <p className="font-medium">₹{rental.depositAmount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-medium">{new Date(rental.startDate).toLocaleDateString()}</p>
                </div>
              </div>
              {rental.endDate && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium">{new Date(rental.endDate).toLocaleDateString()}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {rentals.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No rentals found</p>
        </div>
      )}
    </div>
  );
}